import classNames from 'classnames';
import {
    Icon,
    InfoPanel,
    Modal,
    TimeInput,
} from '@instinet-analytics/semantic-ui-components';
import React, { useCallback, useRef, useState } from 'react';

import './TimeRangeModal.scss';
import { Time } from '@instinet-analytics/core-lib';

export function TimeRangeModal({
    data,
    onChanged,
}: {
    data: { fromTime: Time | undefined; endTime: Time | undefined };
    onChanged: (startTime: Time | undefined, endTime: Time | undefined) => void;
}) {
    const { fromTime, endTime } = data;

    const [fromInputValue, setFromInputValue] = useState<Time | undefined>(
        fromTime
    );
    const [endInputValue, setEndInputValue] = useState<Time | undefined>(
        endTime
    );
    const [error, setError] = useState<string | undefined>(undefined);
    const [fromInputValid, setFromInputValid] = useState(true);
    const [endInputValid, setEndInputValid] = useState(true);

    const triggerRef = useRef<HTMLDivElement | null>(null);

    const trigger = (
        <div className={classNames('TimeRangeModal-trigger')} ref={triggerRef}>
            <Icon name="edit" />
        </div>
    );

    const onOpen = () => {
        setFromInputValue(fromTime);
        setEndInputValue(endTime);
        setError('');
    };

    const onSave = useCallback(() => {
        if (fromInputValue !== undefined && !fromInputValid) {
            setError('Enter a valid "From" time');
            return { cancel: true };
        }
        if (endInputValue !== undefined && !endInputValid) {
            setError('Enter a valid "To" time');
            return { cancel: true };
        }
        if (isLessThan(endInputValue, fromInputValue)) {
            setError('End time must be after from time');
            return { cancel: true };
        }
        onChanged(fromInputValue, endInputValue);
    }, [
        onChanged,
        endInputValid,
        endInputValue,
        fromInputValid,
        fromInputValue,
    ]);

    const onFromValueChanged = (t: Time | undefined) => {
        setFromInputValue(t);
    };
    const onEndValueChanged = (t: Time | undefined) => {
        setEndInputValue(t);
    };
    const onFromInputValidationStatusChanged = (isValid: boolean) =>
        setFromInputValid(isValid);
    const onEndInputValidationStatusChanged = (isValid: boolean) =>
        setEndInputValid(isValid);

    return (
        <Modal
            trigger={trigger}
            cancelText="Cancel"
            headerText="Edit Time Range"
            saveText={'OK'}
            allowCancel={true}
            onSave={onSave}
            onOpen={onOpen}
            className="TimeRangeModal"
        >
            <div className="TimeRangeModal-content ">
                <div className="field">
                    <div>From</div>
                    <TimeInput
                        time={fromInputValue}
                        onChange={onFromValueChanged}
                        onValidationStatusChanged={
                            onFromInputValidationStatusChanged
                        }
                    />
                </div>
                <div className="field">
                    <div>To</div>
                    <TimeInput
                        time={endInputValue}
                        onChange={onEndValueChanged}
                        onValidationStatusChanged={
                            onEndInputValidationStatusChanged
                        }
                    />
                </div>
                {error && <InfoPanel message={error} />}
            </div>
        </Modal>
    );
}

function isLessThan(x: Time | undefined, y: Time | undefined) {
    if (x === undefined || y == undefined) {
        return;
    }
    if (x.hours < y.hours) {
        return true;
    }
    if (x.hours === y.hours) {
        return x.mins < y.mins;
    }
    return false;
}
